<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

// Imports
import loadForm from './loadForm'
import { loadMarketoForms2 } from '~/components/globals/marketo/loadForms2'
import { whenOneTrustReady } from '~/utils/onetrust'
import phoneReplacement from './phoneReplacement'
import 'intl-tel-input/build/css/intlTelInput-no-assets.css'
import '@/styles/intl-tel-input.css'

// Props
const props = defineProps({
  formSettings: {
    type: Object,
    default: () => ({})
  }
})

// Refs
const form = ref(null)
const observer = ref(null)
const cleared = ref(false)
const phoneReplacementCalled = ref(false)
let stopConsentWatch = null
let forms2Requested = false

// Functions
const setRowUtilityClass = () => {
  if (!form.value) return

  const rows = Array.from(form.value.querySelectorAll('.mktoFormRow'))

  for (const row of rows) {
    const inputFields = Array.from(row.querySelectorAll('.mktoFieldDescriptor.mktoFormCol'))

    // Add grid utility to rows that have more than 1 input
    if (inputFields.length > 1) {
      row.classList.add('grid-columns-2')
    }
  }
}

const injectMarketoForms2 = () => {
  if (forms2Requested) return
  forms2Requested = true

  loadMarketoForms2()
    .then(() => {
      const redirect = props.formSettings.redirectLocation?.url
      const { formId } = props.formSettings
      if (!formId) return
      loadForm(redirect, formId, props.formSettings)
      nextTick()
    })
    .catch((error) => {
      console.error('Error loading Marketo form:', error)
    })
}

// Lifecycle
onMounted(() => {
  stopConsentWatch = whenOneTrustReady(injectMarketoForms2)

  // Set up MutationObserver to watch for form changes
  observer.value = new MutationObserver(() => {
    if (!cleared.value) {
      setRowUtilityClass()

      // Call phoneReplacement only once
      if (!phoneReplacementCalled.value) {
        const phoneInput = document.querySelector('input[name="Phone"]')
        if (phoneInput && !document.querySelector('.intl-tel-input')) {
          phoneReplacement()
          phoneReplacementCalled.value = true
        }
      }
    }
  })

  // Start observing when form is available
  const startObserving = () => {
    if (form.value) {
      observer.value.observe(form.value, {
        childList: true,
        subtree: true
      })
    } else {
      setTimeout(startObserving, 100)
    }
  }

  startObserving()
})

// Cleanup
onUnmounted(() => {
  stopConsentWatch?.()
  if (observer.value) {
    observer.value.disconnect()
    cleared.value = true
  }
})
</script>

<template>
<div class="marketo-form"
  :data-form-id="formSettings.formId"
  :data-redirect-location="formSettings.redirectLocation?.url"
>
<!--  renders the form when the script is called  -->
  <form :id='`mktoForm_${ formSettings.formId }`' ref="form"></form>
<!--  script  -->
  <div :id="`script-${ formSettings.formId }`"></div>
</div>
</template>

<style scoped>

/* International Phone Input */
.marketo-form :deep(.iti) {
  width: 100%;
}

.marketo-form :deep(.iti + input[name="Phone"]) {
  display: none !important;
}

.marketo-form :deep(.iti__selected-country-primary) {
  padding: 0 6px 0 8px !important;
  border: 1px solid transparent;
  border-right-color: #ccc;
  border-radius: 15px 0 0 15px;
  padding-left: 18px !important;
  padding-right: 8px !important;
  overflow: hidden;
  position: relative;
}

.marketo-form :deep(.iti__selected-country-primary:hover),
.marketo-form :deep(.iti__selected-country-primary:has(.iti__arrow--up)) {
  background-color: #f5f3f0;
  border: 1px solid var(--color-grey-06);
}

.marketo-form :deep(.mktoFieldWrap:has(input.mktoInvalid) .iti__selected-country-primary:hover),
.marketo-form :deep(.mktoFieldWrap:has(input.mktoInvalid) .iti__selected-country-primary:has(.iti__arrow--up)) {
  border-color: var(--color-red);
}

.marketo-form :deep(.mktoFieldWrap:has(input.mktoInvalid) .iti__selected-country-primary) {
  border-right-color: var(--color-red);
}

.marketo-form :deep(.mktoFieldWrap:has(input:focus) .iti__selected-country-primary) {
  border-right-color: var(--color-grey-06);
}

.marketo-form :deep(.iti__flag) {
  max-width: 12px;
  transform: scale(calc(20 / 12));
  transform-origin: center center;
  border-radius: 20px;
  overflow: hidden;
  background-position: calc(var(--iti-flag-offset) - 3px) -1px;
  box-shadow: none;
  border: 1px solid #ccc;
}

.marketo-form :deep(.iti__selected-country-primary .iti__flag) {
  margin-right: 32px;
}

.marketo-form :deep(.iti__arrow) {
  display: block;
  margin-left: 0;
  width: 16px;
  height: 16px;
  border: none;
  position: relative;
  transition: transform 0.3s ease;
  transform-origin: center center;
}

.marketo-form :deep(.iti__arrow::after) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  height: 6px;
  width: 6px;
  border-bottom: 1px solid #0d0d0d;
  border-right: 1px solid #0d0d0d;
  transform: rotate(45deg) translate(-75%, 0);
}

.marketo-form :deep(.iti__arrow.iti__arrow--up) {
  transform: rotate(180deg);
}

.marketo-form :deep(.iti-country-iso) {
  font-size: 14px;
  font-weight: 500;
  color: #0d0d0d;
  text-transform: uppercase;
  font-family: var(--font-family-primary);
  position: absolute;
  left: 40px;
}

.marketo-form :deep(.iti__country-container) {
  pointer-events: auto;
}

.marketo-form :deep(.iti__selected-dial-code) {
  font-size: 14px !important;
  font-weight: 500;
  color: #0d0d0d !important;
  font-family: var(--font-family-primary);
  line-height: 1 !important;
}

.marketo-form :deep(.iti .iti__selected-dial-code) {
  margin-left: 12px;
}

.marketo-form :deep(input:not([type="checkbox"]).intl-tel-input) {
  padding-top: 1px;
  padding-bottom: 2px !important;
  color: #0d0d0d !important;
  position: relative;
}

.marketo-form :deep(.iti__dropdown-content) {
  margin-top: 0;
  border: none !important;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 10px 1px rgba(33, 34, 66, 0.12), 0 3px 14px 2px rgba(33, 34, 66, 0.08), 0 3px 5px -3px rgba(33, 34, 66, 0.04);
}

.marketo-form :deep(.iti__dropdown-content .iti__search-input) {
  margin: 8px !important;
  width: calc(100% - 16px) !important;
  padding-top: 0;
  padding-bottom: 0 !important;
  height: 40px !important;
}

.marketo-form :deep(.iti__dropdown-content .iti__search-input:not([type="checkbox"]):not([disabled]):focus) {
  border-color: #ccc !important;
}

.marketo-form :deep(.iti__country-list) {
  max-height: 274px !important;
}

.marketo-form :deep(.iti__country) {
  padding: 8px 16px 8px 18px !important;
  display: flex;
}

.marketo-form :deep(.iti__country .iti__flag) {
  margin-right: 0;
}

.marketo-form :deep(.iti__country .iti__dial-code) {
  order: 1;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  font-family: var(--font-family-primary);
  color: #0d0d0d !important;
  margin-left: 11px;
  margin-right: 8px;
}

.marketo-form :deep(.iti__country .iti__country-name) {
  order: 2;
  font-size: 14px;
  line-height: 20px;
  color: #0d0d0d !important;
}

.marketo-form :deep(.iti__country .iti__country-name span) {
  font-family: var(--font-family-primary);
  font-weight: 700;
}

/* Marketo Form */

.marketo-form :deep(.mktoForm div),
.marketo-form :deep(.mktoForm span),
.marketo-form :deep(.mktoForm label),
.marketo-form :deep(.mktoForm p) {
  text-align: left;
  margin: 0;
  padding: 0;
}

.marketo-form :deep(form) {
  width: 100% !important;
  font-family: var(--font-family-primary) !important;
  font-size: 16px !important;
  color: var(--color-gray-700) !important;
  padding: 0 !important;
}

.marketo-form :deep(.mktoFieldWrap),
.marketo-form :deep(.mktoFormCol) {
  width: 100% !important;
}

.marketo-form :deep(.mktoFieldWrap) {
  display: flex;
  flex-direction: column;
}

.marketo-form :deep(.mktoFormCol) {
  margin-bottom: 16px !important;
}

.marketo-form :deep(fieldset) {
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

.marketo-form :deep(.mktoLabel) {
  display: flex;
  flex-direction: row;
  color: var(--color-gray-500);
  font-size: 16px;
  font-weight: 500 !important;
  letter-spacing: -.3px;
  line-height: 24px;
  margin-bottom: 8px !important;
  padding-top: 0 !important;
  width: auto !important;
}

.marketo-form :deep(.mktoRequiredField .mktoLabel::after) {
  content: "*";
  height: 100%;
  margin-left: 5px;
  width: 10px;
  color: var(--color-error);
}

.marketo-form :deep(.mktoField:not([type="checkbox"])),
.marketo-form :deep(input:not([type="checkbox"])) {
  padding: 2px 10px 3px 14px;
  background: #fff;
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  font-size: 14px;
  height: 48px;
  letter-spacing: -.2px;
  line-height: 26px;
  width: 100% !important;
  color: #000 !important;
}

.marketo-form :deep(select.mktoField):focus {
  border-color: #0d0d0d !important;
  outline: none !important;
  transition-duration: .1s !important;
}

.marketo-form :deep(.mktoField:not([type="checkbox"]))::placeholder,
.marketo-form :deep(input:not([type="checkbox"]))::placeholder {
  color: var(--color-gray-250) !important;
}

.marketo-form :deep(input:not([type=checkbox]):not([disabled]):focus) {
  background: transparent !important;
  border-color: #0d0d0d !important;
  outline: none !important;
  transition-duration: .1s !important;
}

.marketo-form :deep(input:not([type=checkbox]):not([disabled]):focus-visible) {
  outline: 2px solid var(--color-black-100) !important;
}

.marketo-form :deep(input.mktoInvalid.mktoRequired.mktoRequired:not([type="checkbox"])) {
  border: 1px solid var(--color-error) !important;
}

.marketo-form :deep(select.mktoField) {
  appearance: none;
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  opacity: 1;
}

.marketo-form :deep(select.mktoField option) {
  font-family: var(--font-family-primary) !important;
}

.marketo-form :deep(.mktoForm .mktoError) {
  position: relative !important;
  bottom: inherit !important;
  right: inherit !important;
  color: var(--color-error) !important;
  pointer-events: none;
  z-index: 0 !important;
}

.mktoForm :deep(.mktoError .mktoErrorArrowWrap) {
  display: none;
}

.marketo-form :deep(.mktoForm .mktoErrorMsg) {
  display: inline-block;
  margin-top: 2px;
  background-color: transparent;
  background-image: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  color: var(--color-error);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -.2px;
  line-height: 20px;
  max-width: inherit;
  padding: 0;
  text-shadow: none;
  transform: translateY(2px);
}

.mktoForm :deep(.--err-marketo-business-email) {
  bottom: 0;
  color: var(--color-error);
  font-size: 12px;
  line-height: 140%; /* 16.8px */
  letter-spacing: -0.24px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transform: translateY(100%);
  transition: all .2s ease;
}

.marketo-form :deep(.--err-marketo-business-email.--active) {
    opacity: 1;
    pointer-events: auto;
}

.marketo-form :deep(.mktoLogicalField.mktoCheckboxList.mktoHasWidth) {
  display: flex;
  align-items: flex-start;
  border-bottom: none;
  flex-direction: row;
  margin-bottom: 0;
  padding: 8px 0;
  width: 100% !important;
}

.marketo-form :deep(input[type="checkbox"]) {
  appearance: none;
  background: transparent;
  border: 1px solid #D9D6D2;
  border-radius: 4px;
  cursor: pointer;
  height: 20px;
  width: 20px;
  margin-right: 8px;
  position: relative;
  transition: all .3s ease;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.marketo-form :deep(input[type="checkbox"]:checked) {
  background: var(--color-black);
  border-color: var(--color-black);
}

.marketo-form :deep(input[type="checkbox"]:after) {
  content: '';
  position: absolute;
  width: 14px;
  height: 8px;
  border-left: 2px solid #D9D6D2;
  border-bottom: 2px solid #D9D6D2;
  left: 50%;
  top: 50%;
  transform: translate(-46%, -63%) rotate(-45deg) scale(0.7);
  transition: opacity 0.3s ease;
}

.marketo-form :deep(input[type="checkbox"]:checked:after) {
  border-color: #fff !important;
}

.marketo-form :deep(.mktoForm .mktoCheckboxList > label),
.marketo-form :deep(.mktoForm .mktoCheckboxList label),
.marketo-form :deep(.mktoForm input[type="checkbox"] + label),
.marketo-form :deep(.mktoHtmlText.mktoHasWidth) {
  color: #000 !important;
  display: block !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  letter-spacing: -.2px !important;
  line-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.marketo-form :deep(a) {
  color: #0d0d0d !important;
  font-weight: 500 !important;
  padding: 0 !important;
  text-decoration: underline !important;
}

.marketo-form :deep(.mktoButtonRow) {
  display: flex;
  height: 48px;
  justify-content: center;
  text-align: center;
  width: 100%;
}

.marketo-form :deep(.mktoButtonWrap) {
  margin: 0 !important;
}

.marketo-form :deep(.mktoButtonWrap.mktoSimple .mktoButton),
.marketo-form :deep(.mktoButtonWrap.mktoDownloadButton .mktoButton) {
  height: 48px !important;
  min-width: 160px !important;
  background: #000 !important;
  border: none;
  border-radius: 8px !important;
  box-sizing: border-box;
  color: #fff;
  cursor: pointer;
  font-family: var(--font-family-primary) !important;
  font-size: 16px !important;
  font-weight: 500;
  line-height: 22px;
  margin: 0 !important;
  padding-bottom: 0 !important;
  padding-top: 0 !important;
  position: relative;
  text-align: center !important;

  @media (min-width: 1024px) {
    height: 52px;
    font-size: 18px !important;
    line-height: 140% !important; /* 25.2px */
    letter-spacing: -0.18px !important;
  }
}

.marketo-form :deep(.mktoButtonWrap.mktoSimple .mktoButton:focus-visible) {
  outline: 2px solid var(--color-black-100) !important;
  outline-offset: 2px;
}

.marketo-form :deep(.mktoForm .mktoOffset),
.marketo-form :deep(.mktoForm .mktoGutter) {
   display: none !important;
}

@media (min-width: 768px) {
  .marketo-form :deep(.mktoFormRow.grid-columns-2) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .marketo-form :deep(.grid-columns-2 > .mktoFormCol) {
    width: calc(50% - 6px) !important;
  }
}

</style>

